import type IMatrixConfig from '@/components/views/Matrix/classes/IMatrixConfig';

type RaceID = number;
type StationRoles = number[];
type RaceGroupIDs = number[];
type CategoryIDs = number[];

type StationRolesMap = Map<RaceID, StationRoles>;
type RaceGroupMap = Map<RaceID, RaceGroupIDs>;
type CategoryMap = Map<RaceID, CategoryIDs>;

/**
 * This is the matrix config wrapped in a settings class so we can save it to the db.
 * The IMatrixConfig needs to be split into different parts so we dont lose data when we change the race.
 * The split is as follows:
 * StationRoles, RaceGroups and Categories are per race, so we save them in their own maps.
 * The RaceID should always be NULL when we load from db, so we start without an inital race chosen.
 * The rest of the settings are global and should be saved as is.
 */
export class MatrixSettings {
    static NAME_OF_CLASS = 'MatrixSettings';
    public MatrixConfig: IMatrixConfig;

    public StationRolesMap: StationRolesMap;
    public RaceGroupMap: RaceGroupMap;
    public CategoryMap: CategoryMap;

    public constructor(args: {
        stationRolesMap: StationRolesMap;
        raceGroupMap: RaceGroupMap;
        categoryMap: CategoryMap;
        matrixConfig: IMatrixConfig;
    }) {
        this.StationRolesMap = args.stationRolesMap;
        this.RaceGroupMap = args.raceGroupMap;
        this.CategoryMap = args.categoryMap;

        this.MatrixConfig = args.matrixConfig;

        // Reset the RaceID so we dont have a preselected race
        this.MatrixConfig.RaceID = null;

        // Reset search so we dont have a saved search
        this.MatrixConfig.SearchString = '';
    }

    /**
     * Every time we change the race we save the current values in the lookup maps and load any saved values that match the new race
     * @param oldRaceID The race we changed from. The current values in StationRoles, RaceGroups and Categories are for this race.
     * @param newRaceID The race we are changing to. We need to overwrite the StationRoles, RaceGroups and Categories with any saved values for this race
     */
    public RaceChanged(oldRaceID: RaceID | null, newRaceID: RaceID | null): void {
        // If we have an old value we save the old value in the lookup maps, with the current selection
        if (oldRaceID) {
            this.StationRolesMap.set(oldRaceID, this.MatrixConfig.StationRoleIDs);
            this.RaceGroupMap.set(oldRaceID, this.MatrixConfig.RaceGroupIDs);
            this.CategoryMap.set(oldRaceID, this.MatrixConfig.CategoryIDs);
        }

        if (oldRaceID === newRaceID) {
            return;
        }

        // Load whatever previous selection
        this.MatrixConfig.StationRoleIDs = newRaceID ? (this.StationRolesMap.get(newRaceID) ?? []) : [];
        this.MatrixConfig.RaceGroupIDs = newRaceID ? (this.RaceGroupMap.get(newRaceID) ?? []) : [];
        this.MatrixConfig.CategoryIDs = newRaceID ? (this.CategoryMap.get(newRaceID) ?? []) : [];
    }
}
